import { useEffect } from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import LeadForm from "../components/LeadForm";
import { SectionEyebrow, FadeInSection } from "../components/SharedSections";

const CHRIS_HEADSHOT = "https://static.wixstatic.com/media/8cad63_5d40e20bba3647b6b3c050cd2f415da5~mv2.jpg/v1/fill/w_150,h_150,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/8cad63_5d40e20bba3647b6b3c050cd2f415da5~mv2.jpg";

const ContactPage = () => {
  useEffect(() => {
    document.title = "Contact Mayker Financial Group | Victoria BC";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", "Get in touch with Mayker Financial Group. Book a free 15-minute strategy call with our independent financial advisors in Victoria, BC.");
    }
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="bg-[#0C1B33] pt-28 lg:pt-32 pb-12 lg:pb-16 px-4 lg:px-12 relative text-center">
        <div className="hero-bottom-line" />
        <div className="max-w-2xl mx-auto">
          <FadeInSection>
            <h1 className="font-playfair text-3xl lg:text-5xl font-bold text-white leading-tight mb-4">
              Let's talk.
            </h1>
            <p className="text-base lg:text-lg text-white/60 leading-relaxed">
              Whether you have a quick question or you're ready to build your financial plan — we're here. Reach out and we'll be in touch within one business day.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-[#FAF8F3] py-12 lg:py-16 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Left - Form */}
            <FadeInSection>
              <div className="bg-white rounded-xl p-6 lg:p-8 border border-black/5 card-top-accent">
                <LeadForm 
                  title="Send us a message"
                  subtitle="We'll get back to you within one business day."
                  phoneRequired
                />
              </div>
            </FadeInSection>

            {/* Right - Contact Info */}
            <FadeInSection>
              <div className="space-y-6">
                <div>
                  <SectionEyebrow>Get in touch</SectionEyebrow>
                  <h2 className="font-playfair text-2xl font-semibold text-[#0C1B33] mb-6">
                    Contact details
                  </h2>
                </div>

                <div className="space-y-4">
                  <a 
                    href="tel:7788361962"
                    className="flex items-center gap-4 p-4 bg-white border border-black/5 rounded-lg hover:border-[#C9A84C]/30 transition-colors"
                  >
                    <div className="w-10 h-10 bg-[#C9A84C]/10 rounded-lg flex items-center justify-center text-[#C9A84C]">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs text-[#6B7A99] mb-0.5">Phone</div>
                      <div className="font-medium text-[#0C1B33]">778-836-1962</div>
                    </div>
                  </a>

                  <a 
                    href="mailto:chris@maykerfinancial.com"
                    className="flex items-center gap-4 p-4 bg-white border border-black/5 rounded-lg hover:border-[#C9A84C]/30 transition-colors"
                  >
                    <div className="w-10 h-10 bg-[#C9A84C]/10 rounded-lg flex items-center justify-center text-[#C9A84C]">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs text-[#6B7A99] mb-0.5">Email</div>
                      <div className="font-medium text-[#0C1B33]">chris@maykerfinancial.com</div>
                    </div>
                  </a>

                  <div className="flex items-center gap-4 p-4 bg-white border border-black/5 rounded-lg">
                    <div className="w-10 h-10 bg-[#C9A84C]/10 rounded-lg flex items-center justify-center text-[#C9A84C]">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs text-[#6B7A99] mb-0.5">Address</div>
                      <div className="font-medium text-[#0C1B33]">607 Vancouver Street, Victoria, BC</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-white border border-black/5 rounded-lg">
                    <div className="w-10 h-10 bg-[#C9A84C]/10 rounded-lg flex items-center justify-center text-[#C9A84C]">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs text-[#6B7A99] mb-0.5">Office Hours</div>
                      <div className="font-medium text-[#0C1B33]">Monday–Friday, 9am–5pm PST</div>
                    </div>
                  </div>
                </div>

                {/* Book a call CTA */}
                <div className="bg-[#0C1B33] rounded-lg p-6 mt-6">
                  <h3 className="font-playfair text-lg font-semibold text-white mb-2">
                    Book a 15-minute strategy call
                  </h3>
                  <p className="text-sm text-white/60 mb-4">
                    Skip the back and forth — pick a time that works for you.
                  </p>
                  <a 
                    href="https://www.maykerfinancial.com/book-online"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center bg-[#C9A84C] text-[#0C1B33] text-sm font-medium py-3 rounded hover:bg-[#E8CC80] transition-colors"
                  >
                    Book Your Free Call →
                  </a>
                </div>

                {/* Chris intro */}
                <div className="flex items-center gap-4 p-4 bg-white border border-black/5 rounded-lg mt-4">
                  <img 
                    src={CHRIS_HEADSHOT}
                    alt="Chris Johnstone"
                    className="w-12 h-12 rounded-full object-cover object-top border-2 border-[#C9A84C]"
                  />
                  <div>
                    <div className="text-sm font-medium text-[#0C1B33]">You'll likely be speaking with Chris or Kai</div>
                    <div className="text-xs text-[#6B7A99]">Your dedicated advisors at Mayker</div>
                  </div>
                </div>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="bg-[#F0EDE4] py-12 lg:py-16 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <div className="rounded-lg overflow-hidden shadow-lg">
              <iframe
                title="Mayker Financial Group Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2644.5!2d-123.3615!3d48.4284!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x548f749b7d67b4dd%3A0x88b2342f63e6d3b0!2s607%20Vancouver%20St%2C%20Victoria%2C%20BC!5e0!3m2!1sen!2sca!4v1700000000000!5m2!1sen!2sca"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </FadeInSection>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
